import classNames from "classnames";
import { CSSProperties, Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { I18n } from "../../hooks/useTranslations";
import { Glyph } from "../../types";
import SvgDelete from "../icons/Delete";
import SvgDownload from "../icons/Download";
import SvgEdit from "../icons/Edit";
import SvgMoreVert from "../icons/MoreVert";
import SvgShieldLock from "../icons/ShieldLock";
import Icon from "./Icon";
import styles from "./ListItemMenu.module.css";
import { Menu } from "./Menu";

// type XOR<T, U> = (T & { [K in keyof U]?: never }) | (U & { [K in keyof T]?: never });

export type Action = {
  onClick: () => void;
  isDisabled?: boolean;
  title: string;
  isDivider?: boolean;
  icon?: Glyph;
  color?: "red" | "blue" | "green";
  visible?: boolean;
};

export const getDeleteAction = (i18n: I18n, onClick: () => void, isDivider = false, isDisabled?: boolean): Action => ({
  onClick,
  title: i18n.translation.common.delete,
  color: "red",
  icon: SvgDelete,
  isDivider,
  isDisabled,
});

export const getRemoveAction = (i18n: I18n, onClick: () => void, isDivider = false, isDisabled?: boolean): Action => ({
  onClick,
  title: i18n.translation.common.remove,
  color: "red",
  icon: SvgDelete,
  isDivider,
  isDisabled,
});

export const getDownloadAction = (i18n: I18n, onClick: () => void, isDivider = false): Action => ({
  onClick,
  title: i18n.translation.common.download,
  icon: SvgDownload,
  isDivider,
});

export const getEditAction = (i18n: I18n, onClick: () => void, isDivider = false): Action => ({
  onClick,
  title: i18n.translation.common.edit,
  icon: SvgEdit,
  isDivider,
});

export const getAccessesAction = (i18n: I18n, onClick: () => void): Action => ({
  onClick,
  title: i18n.translation.entityAccesses.title,
  icon: SvgShieldLock,
});

interface ListItemMenuProps<T> {
  actions: Action[];
  item: T;
}

const VIEWPORT_MARGIN = 16;
const PANEL_GAP = 6;

const ListItemMenu = <T,>({ actions }: ListItemMenuProps<T>) => {
  const visibleActions = actions.filter((action) => action.visible ?? true);
  const [isModalOpen, setModalOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const clickPointRef = useRef<{ x: number; y: number } | null>(null);

  useOnClickOutside(menuRef, () => setModalOpen(false));

  useEffect(() => {
    if (!isModalOpen) {
      setPanelStyle(null);
      return;
    }

    const updatePosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      if (!triggerRect) {
        return;
      }

      const panelWidth = menuRef.current?.offsetWidth ?? 0;
      const panelHeight = menuRef.current?.offsetHeight ?? 0;
      const clickPoint = clickPointRef.current;

      if (clickPoint) {
        const wouldOverflowRight = clickPoint.x + panelWidth > window.innerWidth - VIEWPORT_MARGIN;
        const wouldOverflowBottom = clickPoint.y + panelHeight > window.innerHeight - VIEWPORT_MARGIN;
        const hasRoomAbove = clickPoint.y - panelHeight >= VIEWPORT_MARGIN;
        const openUp = wouldOverflowBottom && hasRoomAbove;

        setPanelStyle({
          position: "fixed",
          top: openUp ? clickPoint.y - panelHeight : clickPoint.y,
          left: wouldOverflowRight ? clickPoint.x - panelWidth : clickPoint.x,
        });
        return;
      }

      const wouldOverflowLeft = triggerRect.right - panelWidth < VIEWPORT_MARGIN;
      const wouldOverflowBottom = triggerRect.bottom + PANEL_GAP + panelHeight > window.innerHeight - VIEWPORT_MARGIN;
      const hasRoomAbove = triggerRect.top - PANEL_GAP - panelHeight >= VIEWPORT_MARGIN;
      const openUp = wouldOverflowBottom && hasRoomAbove;

      setPanelStyle({
        position: "fixed",
        top: openUp ? triggerRect.top - PANEL_GAP - panelHeight : triggerRect.bottom + PANEL_GAP,
        left: wouldOverflowLeft ? triggerRect.left : triggerRect.right - panelWidth,
      });
    };

    updatePosition();

    const onScroll = () => setModalOpen(false);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isModalOpen]);

  return (
    <div>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={(event) => {
          event.stopPropagation();
          clickPointRef.current = event.detail === 0 ? null : { x: event.clientX, y: event.clientY };
          setModalOpen((open) => !open);
        }}
      >
        <Icon glyph={SvgMoreVert} className={styles.icon} />
      </button>
      {isModalOpen &&
        createPortal(
          <Menu
            ref={menuRef}
            className={styles.menu}
            style={panelStyle ?? { position: "fixed", top: 0, left: 0, visibility: "hidden" }}
          >
            <ul className={styles.list}>
              {visibleActions.map((action, index) => {
                const isDisabled = action.isDisabled;

                return (
                  <Fragment key={index}>
                    {action.isDivider && index > 0 && <div className={styles.divider} />}

                    <li
                      className={
                        isDisabled
                          ? styles.disabled
                          : classNames({
                              [styles.green]: action.color === "green",
                              [styles.red]: action.color === "red",
                              [styles.blue]: action.color === "blue",
                            })
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDisabled) return;
                        setModalOpen(false);
                        action.onClick();
                      }}
                    >
                      {action.icon && <Icon glyph={action.icon} className={styles.icon} />}
                      {action.title}
                    </li>
                  </Fragment>
                );
              })}
            </ul>
          </Menu>,
          document.body,
        )}
    </div>
  );
};

export default ListItemMenu;
